import * as React from 'react';

import { CommonStyles } from '../../common/react/CommonStyles';
import { FilteringListBox } from '../../common/react/components/FilteringListBox';
import { DeprecatedReactMenu } from '../../common/react/components/Menu';
import { SparseCachedList } from '../../common/react/SparseCachedList';
import { IFilterControl } from '../../ts/controllers/FilterPanelCtrl';
import { FieldValuesQuery } from '../../ts/resources/FieldValuesQuery';
import { IFieldValue } from '../../ts/ServerApi/types/IFieldValue';
import { ITypeaheadFilterDefinition } from '../../ts/services/FilterPanelService';
import { TypeaheadFilterButton } from './TypeaheadFilterButton';
import { TypeaheadFilterPanel } from './TypeaheadFilterPanel';

export class TypeaheadFilter extends React.Component<TypeaheadFilter.Props, TypeaheadFilter.State> {

  static displayName = 'TypeaheadFilter';

  static element = React.createFactory(TypeaheadFilter);

  private cachedFieldValues: SparseCachedList<FilteringListBox.Item<IFieldValue>, IFieldValue>;

  private lastFilter: string = null;

  state: TypeaheadFilter.State = {
    fieldValues: []
  };

  public render() {
    return DeprecatedReactMenu.element({
      ref: 'menu',
      tbTestId: ['typeahead-filter', this.props.filter.definition.name].join('-'),
      style: {
        // The width of the menu button should be equal to the width of the containing filter control
        width: '100%'
      },
      menuPanelStyle: {
        // The width of the panel should be equal to the width of the containing filter control
        width: '100%',

        // Overlap the top border of the menu panel with the bottom border of the menu button
        marginTop: -CommonStyles.Sizes.TextBoxBorderSize
      },
      menuButtonFactory: (props?: TypeaheadFilterButton.Props) => {
        props.buttonTitle = this.props.filter.value ? this.props.filter.value.label : '';
        return TypeaheadFilterButton.element(props);
      },
      menuPanelFactory: (props?: TypeaheadFilterPanel.Props<IFieldValue>) => {

        props.itemStore = {
          getItems: (filter: string, start: number, end: number) => {
            if (filter !== this.lastFilter) {
              this.initializeCachedFieldValues(filter);
            }
            return this.cachedFieldValues.getItems(start, end);
          }
        };

        props.matchPrefixesOnly = (<ITypeaheadFilterDefinition>this.props.filter.definition).matchPrefixesOnly;
        props.itemSelected = () => {
          (<DeprecatedReactMenu>this.refs['menu']).closeMenu();
        };
        return TypeaheadFilterPanel.element(props);
      },
      onBeforeMenuOpen: () => {
        this.resetCachedFieldValues();
        return Q.when();
      }
    });

  }

  private resetCachedFieldValues() {
    this.initializeCachedFieldValues('');
  }

  private initializeCachedFieldValues(searchTerm: string) {
    const location = window.location.href;
    this.cachedFieldValues = new SparseCachedList(
      this.props.getFieldValueQuery().withMatchText(searchTerm),
      (item: IFieldValue) => {
        return {
          label: item.label,
          count: item.count,
          url: this.addParameter(location, this.props.filter.definition.name, item.value),
          rawItem: item,
        };
      });
    this.lastFilter = searchTerm;
  }

  // TODO: this may be useful elsewhere - consider moving to a helper class
  // Adapted from the answer at http://stackoverflow.com/a/7648182
  private addParameter(originalUrl: string, param: string, value: string) {
    // Using a positive lookahead (?=\=) to find the
    // given parameter, preceded by a ? or &, and followed
    // by a = with a value after than (using a non-greedy selector)
    // and then followed by a & or the end of the string
    const val = new RegExp('(\\?|\\&)' + param + '=.*?(?=(&|$))'),
      parts = originalUrl.toString().split('#'),
      url = parts[0],
      hash = parts[1],
      qstring = /\?.+$/;

    // Check if the parameter exists
    if (val.test(hash))
    {
      // if it does, replace it, using the captured group
      // to determine & or ? at the beginning
      return url + '#' + hash.replace(val, '$1' + param + '=' + encodeURIComponent(value));
    }
    else if (qstring.test(hash))
    {
      // otherwise, if there is a query string at all
      // add the param to the end of it
      return originalUrl + '&' + param + '=' + encodeURIComponent(value);
    }
    else
    {
      // if there's no query string, add one
      return originalUrl + '?' + param + '=' + encodeURIComponent(value);
    }
  }

}

export module TypeaheadFilter {

  export interface Props {
    filter: IFilterControl;
    getFieldValueQuery: () => FieldValuesQuery;
  }

  export interface State {
  }

}
